/**
 * Stable device identifier for the `X-Device-Id` request header (DEV-11834).
 *
 * Requirements:
 *   - Stable across page loads, browser restarts, and login/logout.
 *   - Available before the user is authenticated.
 *   - Resolves synchronously so request interceptors can run on the hot path
 *     without awaiting.
 *
 * Strategy: random UUID generated on first visit, persisted to `localStorage`.
 *
 *   1. Module-level in-memory cache (zero work after the first call in a tab).
 *   2. `localStorage` (survives reloads and browser restarts on the same
 *      browser profile).
 *   3. Generate a fresh UUID when storage is empty or unreachable.
 *
 * Caveat — clearing site data or using incognito gives a new device id. This
 * is a deliberate trade-off for simplicity and zero fingerprinting surface;
 * the header is correlation metadata, not an identity assertion.
 */

const STORAGE_KEY = 'heidi_device_id';
const FALLBACK = 'unknown';

let memoryCache: string | null = null;

function safeLocalStorageGet(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // localStorage may be blocked (incognito, quota exceeded, Safari ITP).
    // The in-memory cache is still populated, so the device id stays stable
    // for the lifetime of the tab.
  }
}

function generateDeviceId(): string {
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
    return FALLBACK;
  }
  try {
    return crypto.randomUUID();
  } catch {
    return FALLBACK;
  }
}

/**
 * Returns the device id synchronously. Safe to call from axios request
 * interceptors. Never throws.
 */
export function getDeviceId(): string {
  if (memoryCache) return memoryCache;

  const stored = safeLocalStorageGet();
  if (stored) {
    memoryCache = stored;
    return stored;
  }

  const generated = generateDeviceId();
  memoryCache = generated;
  if (generated !== FALLBACK) {
    safeLocalStorageSet(generated);
  }
  return generated;
}

/** @internal — for tests only. Resets the module-level cache. */
export function __resetDeviceIdCacheForTesting(): void {
  memoryCache = null;
}
